/**
 * useCanvasGraph.ts — owns all interactive state for the workflow canvas:
 * node positions/drag, edge drawing/connect, selection, delete, and
 * debounced persistence of the graph back to the API.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { updateWorkflow } from "../../services/workflow.service";
import { NODE_WIDTH, applyResize, nextFreePosition } from "./canvasGeometry";
import type { ResizeDirection } from "./canvasGeometry";
import { useEdgeDrawing } from "./useEdgeDrawing";
import type {
  NodeDef,
  WorkflowDefinition,
  WorkflowEdge,
  WorkflowNode,
} from "../workflow.types";

interface UseCanvasGraphOptions {
  workflow: WorkflowDefinition | null;
  onPersisted: (updated: WorkflowDefinition) => void;
}

let localIdCounter = 0;
function makeLocalId(prefix: string) {
  localIdCounter += 1;
  return `${prefix}-${Date.now()}-${localIdCounter}`;
}

export function useCanvasGraph({ workflow, onPersisted }: UseCanvasGraphOptions) {
  const [nodes, setNodes] = useState<WorkflowNode[]>(workflow?.nodes ?? []);
  const [edges, setEdges] = useState<WorkflowEdge[]>(workflow?.edges ?? []);
  // Two independent ideas that used to share one state variable: `selectedNodeId`
  // is the canvas highlight (double-click or drag), `panelNodeId` is which
  // node's properties panel is showing (single click) — a quick single click to
  // peek at a node's config no longer also marks it "selected" on canvas.
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [panelNodeId, setPanelNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [resizingNodeId, setResizingNodeId] = useState<string | null>(null);
  const [paletteHint, setPaletteHint] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const dragRef = useRef<{ nodeId: string; offsetX: number; offsetY: number; startX: number; startY: number } | null>(
    null,
  );
  const dragMovedRef = useRef(false);
  const resizeRef = useRef<{
    nodeId: string;
    direction: ResizeDirection;
    startRect: { x: number; y: number; width: number; height: number };
    startClientX: number;
    startClientY: number;
  } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  nodesRef.current = nodes;
  edgesRef.current = edges;

  useEffect(() => {
    setNodes(workflow?.nodes ?? []);
    setEdges(workflow?.edges ?? []);
    setSelectedNodeId(null);
    setPanelNodeId(null);
    setSelectedEdgeId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow?._id]);

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const registerNodeRef = useCallback((id: string, el: HTMLDivElement | null) => {
    nodeRefs.current[id] = el;
  }, []);

  const persistNow = useCallback(
    (nextNodes: WorkflowNode[], nextEdges: WorkflowEdge[]) => {
      if (!workflow) return;
      updateWorkflow(workflow._id, { nodes: nextNodes, edges: nextEdges })
        .then((res) => {
          if (res?.data) onPersisted(res.data);
        })
        .catch(() => {
          /* best-effort — local canvas state stays authoritative until the next load */
        });
    },
    [workflow, onPersisted],
  );

  const schedulePersist = useCallback(
    (delay = 400) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        persistNow(nodesRef.current, edgesRef.current);
      }, delay);
    },
    [persistNow],
  );

  const selectNode = useCallback((id: string | null) => {
    setSelectedNodeId(id);
    setSelectedEdgeId(null);
    // Double-click/drag selecting a node is a stronger interaction than a
    // peek click — it also shows that node's properties.
    if (id) setPanelNodeId(id);
  }, []);

  const openPanel = useCallback((id: string | null) => {
    setPanelNodeId(id);
  }, []);

  const selectEdge = useCallback((id: string | null) => {
    setSelectedEdgeId(id);
    setSelectedNodeId(null);
    setPanelNodeId(null);
  }, []);

  // ── Zoom ────────────────────────────────────────────────────────────────
  // A CSS-transform scale on the canvas content — node/edge x,y stay in the
  // same logical coordinate space regardless of zoom (WorkflowCanvas scales
  // the whole content wrapper), so only screen->canvas point conversion below
  // needs to know about it.
  const [zoom, setZoomState] = useState(1);
  const zoomRef = useRef(1);
  zoomRef.current = zoom;
  const MIN_ZOOM = 0.4;
  const MAX_ZOOM = 1.75;

  const setZoom = useCallback((next: number) => {
    setZoomState(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next)));
  }, []);
  const zoomIn = useCallback(() => setZoom(zoomRef.current + 0.15), [setZoom]);
  const zoomOut = useCallback(() => setZoom(zoomRef.current - 0.15), [setZoom]);
  const resetZoom = useCallback(() => setZoom(1), [setZoom]);

  // ── Node drag ──────────────────────────────────────────────────────────

  const canvasPoint = useCallback((clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: (clientX - rect.left + el.scrollLeft) / zoomRef.current,
      y: (clientY - rect.top + el.scrollTop) / zoomRef.current,
    };
  }, []);

  const beginNodeDrag = useCallback(
    (nodeId: string, clientX: number, clientY: number) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      const p = canvasPoint(clientX, clientY);
      dragRef.current = { nodeId, offsetX: p.x - node.x, offsetY: p.y - node.y, startX: clientX, startY: clientY };
      dragMovedRef.current = false;
      setDraggingNodeId(nodeId);
      // Selection only happens once real movement is detected (see onMove
      // below) or via an explicit double-click — a plain single click on a
      // node must not open the properties panel.
    },
    [canvasPoint],
  );

  useEffect(() => {
    if (!draggingNodeId) return undefined;
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (!dragMovedRef.current) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
        dragMovedRef.current = true;
        selectNode(drag.nodeId);
      }
      const p = canvasPoint(e.clientX, e.clientY);
      const nx = Math.max(0, p.x - drag.offsetX);
      const ny = Math.max(0, p.y - drag.offsetY);
      setNodes((prev) => prev.map((n) => (n.id === drag.nodeId ? { ...n, x: nx, y: ny } : n)));
    };
    const onUp = () => {
      const moved = dragMovedRef.current;
      dragRef.current = null;
      dragMovedRef.current = false;
      setDraggingNodeId(null);
      if (moved) persistNow(nodesRef.current, edgesRef.current);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [draggingNodeId, canvasPoint, persistNow, selectNode]);

  // ── Node resize (8-handle drag on the card) ─────────────────────────────
  // Mirrors the drag pattern above: live local state during the drag, one
  // persisted write on release. The starting rect is read from the live DOM
  // node (not `node.x/y/config.width/height`) so a never-resized node — which
  // has no stored dimensions, just the CSS default — still resizes smoothly
  // from whatever it's actually rendered at, and a north/west drag (which
  // moves x/y, not just width/height) starts from the real current position.

  const beginNodeResize = useCallback(
    (nodeId: string, direction: ResizeDirection, clientX: number, clientY: number) => {
      const el = nodeRefs.current[nodeId];
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!el || !node) return;
      resizeRef.current = {
        nodeId,
        direction,
        startRect: { x: node.x, y: node.y, width: el.offsetWidth, height: el.offsetHeight },
        startClientX: clientX,
        startClientY: clientY,
      };
      setResizingNodeId(nodeId);
    },
    [],
  );

  useEffect(() => {
    if (!resizingNodeId) return undefined;
    const onMove = (e: MouseEvent) => {
      const resize = resizeRef.current;
      if (!resize) return;
      const dx = (e.clientX - resize.startClientX) / zoomRef.current;
      const dy = (e.clientY - resize.startClientY) / zoomRef.current;
      const next = applyResize(resize.direction, resize.startRect, dx, dy);
      setNodes((prev) =>
        prev.map((n) =>
          n.id === resize.nodeId
            ? { ...n, x: next.x, y: next.y, config: { ...n.config, width: next.width, height: next.height } }
            : n,
        ),
      );
    };
    const onUp = () => {
      resizeRef.current = null;
      setResizingNodeId(null);
      persistNow(nodesRef.current, edgesRef.current);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [resizingNodeId, persistNow]);

  // ── Edge drawing (delegated) ────────────────────────────────────────────

  const { drawingEdge, hoveredTargetId, beginEdgeDraw, nodeRect } = useEdgeDrawing({
    nodesRef,
    edgesRef,
    nodeRefs,
    canvasPoint,
    onCreateEdge: (edge: WorkflowEdge) => {
      setEdges((prev) => {
        const next = [...prev, edge];
        persistNow(nodesRef.current, next);
        return next;
      });
    },
  });

  // ── Add / delete ──────────────────────────────────────────────────────

  const addNode = useCallback(
    (def: NodeDef, position?: { x: number; y: number }) => {
      if (def.category === "trigger" && nodesRef.current.some((n) => n.type === "trigger")) {
        setPaletteHint("A workflow can only have one trigger.");
        setTimeout(() => setPaletteHint(null), 2500);
        return;
      }

      // Logical placement: an explicit drop position (drag-and-drop from the
      // palette) is always respected as-is. Otherwise — a plain click on a
      // palette card — chain off whichever node is currently selected rather
      // than always trailing the last-added node, so "select a step, click
      // the next action" reads as building a chain, not scattering nodes.
      const anchorNode = !position ? nodesRef.current.find((n) => n.id === selectedNodeId) : undefined;
      const last = nodesRef.current[nodesRef.current.length - 1];
      const pos = position ?? nextFreePosition((anchorNode ?? last)?.x, (anchorNode ?? last)?.y);
      const type = def.category === "trigger" ? "trigger" : def.category === "logic" ? "condition" : "action";
      const node: WorkflowNode = {
        id: makeLocalId("node"),
        type,
        subtype: def.subtype,
        x: Math.max(0, pos.x - NODE_WIDTH / 2),
        y: Math.max(0, pos.y),
        config: {
          label: def.label,
          args: def.argFields
            ? Object.fromEntries(def.argFields.map((f) => [f.key, f.defaultTemplate]))
            : undefined,
        },
      };

      const nextNodes = [...nodesRef.current, node];
      let nextEdges = edgesRef.current;
      // Auto-connect only when unambiguous: a trigger/action node has exactly
      // one output port. A condition's two branches are never guessed at —
      // the user wires Yes/No by hand.
      if (
        anchorNode &&
        anchorNode.type !== "condition" &&
        !edgesRef.current.some((e) => e.source === anchorNode.id && e.sourcePort === "out")
      ) {
        nextEdges = [
          ...edgesRef.current,
          { id: makeLocalId("edge"), source: anchorNode.id, sourcePort: "out", target: node.id, targetPort: "in" },
        ];
      }

      setNodes(nextNodes);
      if (nextEdges !== edgesRef.current) setEdges(nextEdges);
      persistNow(nextNodes, nextEdges);
      selectNode(node.id);
    },
    [persistNow, selectNode, selectedNodeId],
  );

  const deleteSelected = useCallback(() => {
    if (selectedNodeId) {
      setNodes((prev) => {
        const nextNodes = prev.filter((n) => n.id !== selectedNodeId);
        setEdges((prevEdges) => {
          const nextEdges = prevEdges.filter(
            (e) => e.source !== selectedNodeId && e.target !== selectedNodeId,
          );
          persistNow(nextNodes, nextEdges);
          return nextEdges;
        });
        return nextNodes;
      });
      setSelectedNodeId(null);
      setPanelNodeId((prev) => (prev === selectedNodeId ? null : prev));
    } else if (selectedEdgeId) {
      setEdges((prev) => {
        const next = prev.filter((e) => e.id !== selectedEdgeId);
        persistNow(nodesRef.current, next);
        return next;
      });
      setSelectedEdgeId(null);
    }
  }, [selectedNodeId, selectedEdgeId, persistNow]);

  // ── Duplicate / copy / paste ──────────────────────────────────────────
  // Clipboard holds a snapshot of the node's data (not a live reference), so
  // pasting still works after the original was edited or even deleted.
  const clipboardRef = useRef<WorkflowNode | null>(null);
  const pasteCountRef = useRef(0);

  const cloneNodeAt = useCallback(
    (source: WorkflowNode, x: number, y: number) => {
      if (source.type === "trigger" && nodesRef.current.some((n) => n.type === "trigger")) {
        setPaletteHint("A workflow can only have one trigger.");
        setTimeout(() => setPaletteHint(null), 2500);
        return;
      }
      const clone: WorkflowNode = {
        ...source,
        id: makeLocalId("node"),
        x: Math.max(0, x),
        y: Math.max(0, y),
        config: {
          ...source.config,
          args: source.config.args ? { ...source.config.args } : undefined,
        },
      };
      const nextNodes = [...nodesRef.current, clone];
      setNodes(nextNodes);
      persistNow(nextNodes, edgesRef.current);
      selectNode(clone.id);
    },
    [persistNow, selectNode],
  );

  const duplicateNode = useCallback(
    (nodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === nodeId);
      if (!source) return;
      cloneNodeAt(source, source.x + 40, source.y + 40);
    },
    [cloneNodeAt],
  );

  const copyNode = useCallback((nodeId: string) => {
    const source = nodesRef.current.find((n) => n.id === nodeId);
    if (!source) return;
    clipboardRef.current = source;
    pasteCountRef.current = 0;
  }, []);

  const pasteNode = useCallback(() => {
    const source = clipboardRef.current;
    if (!source) return;
    pasteCountRef.current += 1;
    const offset = pasteCountRef.current * 40;
    cloneNodeAt(source, source.x + offset, source.y + offset);
  }, [cloneNodeAt]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
      const meta = e.metaKey || e.ctrlKey;

      if (meta && e.key.toLowerCase() === "c") {
        if (!selectedNodeId) return;
        e.preventDefault();
        copyNode(selectedNodeId);
        return;
      }
      if (meta && e.key.toLowerCase() === "v") {
        if (!clipboardRef.current) return;
        e.preventDefault();
        pasteNode();
        return;
      }
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (!selectedNodeId && !selectedEdgeId) return;
      e.preventDefault();
      deleteSelected();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedNodeId, selectedEdgeId, deleteSelected, copyNode, pasteNode]);

  const updateNodeConfig = useCallback(
    (nodeId: string, patch: Partial<WorkflowNode["config"]>) => {
      setNodes((prev) => {
        const next = prev.map((n) => (n.id === nodeId ? { ...n, config: { ...n.config, ...patch } } : n));
        schedulePersist();
        return next;
      });
      // Removing a condition option orphans any edge still wired from its
      // port — drop those so nothing points at a port that no longer exists.
      if (patch.options) {
        const validPortIds = new Set(patch.options.map((o) => o.id));
        setEdges((prev) => prev.filter((e) => e.source !== nodeId || validPortIds.has(e.sourcePort)));
      }
    },
    [schedulePersist],
  );

  return {
    nodes,
    edges,
    containerRef,
    registerNodeRef,
    selectedNodeId,
    panelNodeId,
    openPanel,
    selectedEdgeId,
    selectNode,
    selectEdge,
    draggingNodeId,
    beginNodeDrag,
    resizingNodeId,
    beginNodeResize,
    drawingEdge,
    hoveredTargetId,
    beginEdgeDraw,
    addNode,
    deleteSelected,
    duplicateNode,
    copyNode,
    pasteNode,
    updateNodeConfig,
    paletteHint,
    nodeRect,
    canvasPoint,
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
  };
}
